<script lang="ts">
    import Vue from "vue";
    import { Component } from 'vue-facing-decorator';
    import { AlternativeLogEntryReadModel } from "@Alternatives/Alternatives/Domain/readmodels";
    import { dateCompare, tchMarked } from "@common/Common";
    import moment from 'moment';
    import { LogsBrowserViewModel } from "@Alternatives/Alternatives/Components/LogsBrowser";


    @Component({
        props: {
            viewModel: {
                type: Object,
                required: true
            }
        }
    })
    export default class LogsBrowser extends Vue
    {
        get vm(): LogsBrowserViewModel { return this.$props.viewModel as LogsBrowserViewModel; }
        get opened(): boolean { return this.vm?.opened ?? false; }
        get initialized(): boolean { return this.vm.initialized; }

        logEntriesTitle: string | null = null;
        searchLogEntriesPhrase: string | null = null;

        get logEntriesView(): AlternativeLogEntryReadModel[]
        {
            let view = new Array(...this.vm.logs);

            if (this.logEntriesTitle)
            {
                view = view.filter(a => a.Title.toLowerCase() === this.logEntriesTitle.toLowerCase());
            }

            if (this.searchLogEntriesPhrase)
            {
                const phrase = this.searchLogEntriesPhrase.toLowerCase();
                view = view.filter(a => (a.Person && a.Person.toLowerCase().indexOf(phrase) > -1) || (a.Description1 && a.Description1.toLowerCase().indexOf(phrase) > -1));
            }

            return view.sort((a, b) => dateCompare(new Date(a.Created), new Date(b.Created))).reverse();
        }
        
        getHumanizedTimeSince(dateStr: string)
        {
            const date = moment(dateStr);
            return moment.duration(moment().diff(date)).humanize().replace(' ', '&nbsp;');
        }

        formatDate(dateStr: string, format: string)
        {
            const date = moment(dateStr);
            return date.format(format)
        }

        marked(str: string): string
        {
            return tchMarked(str);
        }

        close()
        {
            this.vm.close();
        }
    }

</script>

<template>
    <div class="modal-mask" v-if="opened">
        <div class="modal-wrapper">
            <div class="modal-dialog modal-dialog-scrollable modal-xl">
                <div class="modal-content">
                    <div class="modal-header">
                        <h5 class="modal-title">Browse logs</h5>
                        <button @click="close" type="button" class="close">&times;</button>
                    </div>

                    <div class="modal-body">
                        <div v-if="!initialized"><i class="fa fa-spinner fa-spin"></i> Loading...</div>
                        <div v-else>
                            <div class="row">
                                <div class="col-sm-6">
                                    <input type="search" v-model="searchLogEntriesPhrase" class="form-control" placeholder="Search log..." />
                                </div>

                                <div class="col-sm-6">
                                    <select v-model="logEntriesTitle" class="form-control">
                                        <option :value="null">All</option>
                                        <option value="Note added">Notes</option>
                                        <option value="Email sent">Emails</option>
                                        <option value="Dependency added">Dependencies</option>
                                        <option value="Document added">Documents</option>
                                    </select>
                                </div>
                            </div>
                            <br />

                            <div class="row" v-for="e in logEntriesView" :key="e.Id">
                                <div class="col-sm-2 col-lg-2">
                                    <div style="margin: 15px; text-align: right;">
                                        <span class="fg-color-gray2 small" v-html="getHumanizedTimeSince(e.Created)"></span>
                                        <br />
                                        <span class="fg-color-gray2 small">ago</span>
                                    </div>
                                </div>

                                <div class="col-sm-10 col-lg-10">
                                    <div class="dashboard-box clearfix" style="padding: 15px 30px;">
                                        <div class="pull-left" style="width: 50px;">
                                            <span :class="['fg-color-gray2', 'glyphicon', 'glyphicon-' + e.Icon]" style="font-size: 24px;"></span>
                                        </div>
                                        <div class="pull-left" style="width: calc(100% - 50px);">
                                            <strong>{{e.Title}}</strong><br />
                                            <span class="small fg-color-gray1">
                                                {{formatDate(e.Created, 'MMM Do, YYYY [at] hh:mm a')}} by {{e.Person}}
                                            </span>

                                            <p class="log-content" v-html="marked(e.Description)"></p>

                                            <div v-if="e.Changes && e.Changes.length > 0" style="overflow-x: hidden;">
                                                <table class="table table-striped">
                                                    <thead>
                                                        <tr>
                                                            <th></th>
                                                            <th>Before</th>
                                                            <th>After</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody v-for="ch in e.Changes">
                                                        <tr>
                                                            <td>{{ch.Property}}</td>
                                                            <td style="overflow-x: hidden;">
                                                                <span v-if="ch.PrevValue" v-html="marked(ch.PrevValue)"></span>
                                                                <span v-if="!ch.PrevValue" class="fa fa-question-circle fg-color-gray"></span>
                                                            </td>
                                                            <td style="overflow-x: hidden;">
                                                                <span v-if="ch.CurrValue" v-html="marked(ch.CurrValue)"></span>
                                                                <span v-if="!ch.CurrValue" class="fa fa-question-circle fg-color-gray"></span>
                                                            </td>
                                                        </tr>
                                                    </tbody>
                                                </table>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div class="modal-footer">
                        <button @click="close" type="button" class="btn btn-outline-secondary">Close</button>
                    </div>

                </div>
            </div>
        </div>
    </div>
</template>      


<style scoped>
    .dashboard-box {
        background-color: white;
        padding: 10px 20px;
        border-radius: 2px;
        box-shadow: 0 1px 5px 0 rgba(45,62,80,0.12);
        margin-bottom: 2vh;
    }

    p.log-content img {
        max-height: 200px;
        display: block;
    }
</style>